import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { ClipboardList, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.css";



import SurveyBuilder from "../components/SurveyBuilder";
import SendPanel from "../components/SendPanel";
import MonitorPanel from "../components/MonitorPanel";

const API_BASE ="http://localhost:5000/api/esat";

const STARTER_JSON = {
  title: "New ESAT Survey",
  showQuestionNumbers: "on",
  pages: [
    {
      name: "page1",
      elements: [
        { type: "text", name: "employee_name", title: "Your name", isRequired: true },
        { type: "rating", name: "satisfaction", title: "Overall satisfaction (1-5)", rateMax: 5 },
        { type: "comment", name: "comments", title: "Comments" }
      ]
    }
  ]
};

export default function ESAT() {
  const { user, token } = useContext(AuthContext);
  const ADMIN_ROLES = new Set(["manager", "admin", "compliance", "hr"]);
  const isAdmin = ADMIN_ROLES.has((user?.role || "").toLowerCase());

const [tab, setTab] = useState("mine");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const notify = (msg, kind = "success") => {
    if (kind === "success") { setSuccess(msg); setError(null); }
    else { setError(msg); setSuccess(null); }
    setTimeout(() => { setSuccess(null); setError(null); }, 4000);
  };

  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token || localStorage.getItem("token") || ""}`
  }), [token]);

  // ---------- My assignments ----------
  const [assigned, setAssigned] = useState([]);
  const [openSurvey, setOpenSurvey] = useState(null);

// Make completed surveys read-only and prevent answering a second copy
const surveyModel = useMemo(() => {
  if (!openSurvey?.surveyJSON) return null;

  const m = new Model(JSON.parse(JSON.stringify(openSurvey.surveyJSON)));
  m.clear(true, true);

  // 🔒 If user has another assignment for the same form already completed, lock this one
  const hasCompletedSameForm =
    !!openSurvey?.formId &&
    Array.isArray(assigned) &&
    assigned.some(
      (a) =>
        a.formId === openSurvey.formId &&
        a.status === "completed" &&
        a.assignmentId !== openSurvey.assignmentId
    );

  if (openSurvey.status === "completed" || hasCompletedSameForm) {
    if (openSurvey.answers && typeof openSurvey.answers === "object") {
      m.data = openSurvey.answers;
    }
    m.mode = "display";
    m.showCompletedPage = false;
    return m;
  }

  // Editable pending assignment
  m.mode = "edit";
  m.showCompletedPage = true;
  m.onComplete.add((sender) =>
    submitResponse(openSurvey.assignmentId, sender.data)
  );
  return m;
}, [openSurvey, assigned]);



  const fetchMyAssigned = async () => {
    try {
      setBusy(true);
      const res = await fetch(`${API_BASE}/my-assigned`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAssigned(await res.json());
    } catch (e) {
      notify(`Failed to load surveys: ${e.message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const submitResponse = async (assignmentId, answers) => {
    try {
      const res = await fetch(`${API_BASE}/submit`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ assignmentId, answers })
      });
      if (!res.ok) throw new Error("Submit failed");
      notify("Response submitted. Thank you!");
      setOpenSurvey(null);
      fetchMyAssigned();
    } catch (e) {
      notify(e.message, "error");
    }
  };

  // ---------- Forms, employees, send ----------
  const [forms, setForms] = useState([]);
  const [emps, setEmps] = useState([]);
  const [builderJSON, setBuilderJSON] = useState(STARTER_JSON);

  const fetchForms = async () => {
    const res = await fetch(`${API_BASE}/forms`, { headers: authHeaders });
    if (res.ok) setForms(await res.json());
  };
  const fetchEmployees = async () => {
    const res = await fetch(`${API_BASE}/employees?active=1`, { headers: authHeaders });
    if (res.ok) setEmps(await res.json());
  };
  const saveForm = async () => {
    try {
      const res = await fetch(`${API_BASE}/forms`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ title: builderJSON?.title || "Untitled Survey", json: builderJSON })
      });
      if (!res.ok) throw new Error("Save failed");
      notify("Form saved.");
      await fetchForms();
    } catch (e) {
      notify(e.message, "error");
    }
  };

  useEffect(() => {
    fetchMyAssigned();
    if (isAdmin) { fetchForms(); fetchEmployees(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleBuilderChange = useCallback((json) => setBuilderJSON(json), []);

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="px-6 pt-4 max-w-full mx-auto">
        <div className="rounded-2xl px-5 py-4 bg-black text-white dark:bg-uasYellow dark:text-black border border-black/80 dark:border-yellow-500/40 shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-white/10 dark:bg-black/10 rounded-xl">
                <ClipboardList className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight">SURVEY TOOL</h1>
                <p className="text-xs opacity-80">Answer, build, send, and monitor surveys</p>
              </div>
            </div>
            <Btn
              onClick={() => { fetchMyAssigned(); if (isAdmin) { fetchForms(); fetchEmployees(); } }}
              disabled={busy}
              className="!px-3 !py-2"
              icon={<RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />}
            >
              Refresh
            </Btn>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-full mx-auto">
        {/* Notifications */}
        {(success || error) && (
          <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full">
            <div className={`p-4 rounded-2xl shadow-lg border ${success ? "bg-green-600 text-white border-green-700" : "bg-red-600 text-white border-red-700"}`}>
              <div className="flex items-center gap-3">
                {success ? (<CheckCircle className="w-5 h-5 text-white" />) : (<XCircle className="w-5 h-5 text-white" />)}
                <span className="font-medium">{success || error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6">
          <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-900 p-1 border border-gray-200 dark:border-gray-700 shadow-sm">
            {(["mine", "dashboard", "monitor"]).map((t) => {
              const active = tab === t;
              const locked = t !== "mine" && !isAdmin;
              return (
                <button
                  key={t}
                  disabled={locked}
                  title={(!locked) ? "" : "Managers/Admins only"}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition ${active ? "bg-white dark:bg-laryzeDarkGray text-gray-900 dark:text-white shadow border border-gray-200 dark:border-gray-700" : "text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-800/60"} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => (!locked) && setTab(t)}
                >
                  {t === "mine" ? "My Surveys" : t === "dashboard" ? "Survey Builder" : "Dashboard"}
                </button>
              );
            })}
          </div>
        </div>

        {/* My Surveys */}
        {tab === "mine" && (
          <Card title="Assigned to me" count={assigned.length}>
            <MyAssigned assigned={assigned} onOpen={setOpenSurvey} busy={busy} />
          </Card>
        )}

        {/* Dashboard (builder + send) */}
        {tab === "dashboard" && isAdmin && (
          <div className="space-y-6">
            <Card title="Form Builder" rightSlot={<Btn tone="accent" onClick={saveForm}>Save</Btn>}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-900/30 rounded-2xl border border-gray-200 dark:border-gray-700 p-3">
                  <SurveyBuilder initial={builderJSON} onChange={handleBuilderChange} />
                </div>
                <div>
                  <div className="text-sm font-medium mb-2">Live Preview</div>
                  <div className="border rounded-2xl p-3 dark:border-gray-700 bg-white dark:bg-laryzeDarkGray">
                    <Survey model={new Model(JSON.parse(JSON.stringify(builderJSON)))} />
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Send Survey">
            <SendPanel
  forms={forms}
  emps={emps}
  apiBase={API_BASE}
  authHeaders={authHeaders}
  onSent={(msg) => notify(msg || "Survey sent.")}
  onError={(e) => notify(e?.message || "Sending failed.", "error")}
/>

            </Card>
          </div>
        )}

        {/* Monitor */}
        {tab === "monitor" && isAdmin && (
          <Card title="Monitor">
            <MonitorPanel forms={forms} apiBase={API_BASE} authHeaders={authHeaders} />
          </Card>
        )}
      </div>

      {/* Fill-out Modal */}
      {openSurvey && (
        <FillModal title={openSurvey.title} onClose={() => setOpenSurvey(null)}>
          {surveyModel ? <Survey model={surveyModel} /> : "Loading..."}
        </FillModal>
      )}
    </div>
  );
}

function MyAssigned({ assigned, onOpen, busy }) {
  if (busy && assigned.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-uasYellow mx-auto" />
        <p className="mt-4 text-gray-500 dark:text-gray-300">Loading surveys...</p>
      </div>
    );
  }

  if (assigned.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500 dark:text-gray-300">
        No surveys currently assigned
      </div>
    );
  }

  // Sort: pending first, completed last
  const sorted = [...assigned].sort((a, b) => {
    if (a.status === "completed" && b.status !== "completed") return 1;
    if (a.status !== "completed" && b.status === "completed") return -1;
    return new Date(b.sentAt) - new Date(a.sentAt);
  });


  

  return (
    <ul className="space-y-4">
      {sorted.map((r) => {
        const isDone = r.status === "completed";
        return (
          <li key={r.assignmentId}>
            <div className={[
              "group rounded-2xl border shadow-sm hover:shadow-md transition-shadow",
              isDone
                ? "border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/30"
                : "border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/30",
            ].join(" ")}
            >
              {/* Header */}
              <div className="flex items-start gap-4 p-5">
                <div className={[
                  "p-2.5 rounded-xl",
                  isDone ? "bg-green-600 text-white" : "bg-red-600 text-white",
                ].join(" ")}
                >
                  <ClipboardList className="w-5 h-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{r.title}</h4>
                    <span className={[
                      "px-2.5 py-0.5 rounded-full text-xs border",
                      isDone
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-red-100 text-red-800 border-red-200",
                    ].join(" ")}
                    >
                      {r.status}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                    <span>Sent: {new Date(r.sentAt).toLocaleString()}</span>
                    {r.completedAt && (
                      <span>Completed: {new Date(r.completedAt).toLocaleString()}</span>
                    )}
                  </div>
                </div>

                <div className="pt-1">
                  <Btn onClick={() => onOpen(r)}>
                    {isDone ? "View" : "Open"}
                  </Btn>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------ Reusable UI bits (rounded/soft) ------------ */

function Card({ title, rightSlot, children, count }) {
  return (
    <div className="bg-white dark:bg-laryzeDarkGray rounded-2xl shadow-lg overflow-hidden">
      <div className="px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b border-black/80 dark:border-yellow-500/40 flex items-center justify-between">
        <h3 className="text-sm font-bold">{title}{typeof count === "number" ? ` (${count})` : ""}</h3>
        {rightSlot}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Btn({ children, onClick, disabled, className = "", tone = "default", icon }) {
  const base = "relative inline-flex items-center justify-center overflow-hidden p-0.5 text-sm font-medium rounded-full transition disabled:opacity-60";
  const wrapDefault = "bg-gradient-to-br from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500";
  const wrapAccent = "bg-gradient-to-br from-yellow-600 to-yellow-700 dark:from-uasYellow dark:to-yellow-500";
  const innerDefault = "bg-black text-white dark:bg-transparent dark:text-black group-hover:bg-opacity-0";
  const innerAccent = "bg-yellow-600 text-white dark:bg-transparent dark:text-black group-hover:bg-opacity-0";
  const pad = "px-5 py-2.5"; // pill
  const wrap = tone === "accent" ? wrapAccent : wrapDefault;
  const inner = tone === "accent" ? innerAccent : innerDefault;
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${wrap} ${className}`}>
      <span className={`relative ${pad} rounded-full border border-black/20 dark:border-transparent flex items-center gap-2 ${inner}`}>
        {icon}{children}
      </span>
    </button>
  );
}

function FillModal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4">
      <div className="bg-white dark:bg-laryzeDarkGray rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Sticky Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-black text-white dark:bg-uasYellow dark:text-black border-b border-black/80 dark:border-yellow-500/40 rounded-t-2xl sticky top-0 z-10">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 dark:hover:bg-black/10">✕</button>
        </div>

        {/* Scrollable Content */}
        <div className="p-5 max-h-[80vh] overflow-auto">{children}</div>
      </div>
    </div>
  );
}
